/**
 * ProcurementDashboard — landing KPI board for /procurement.
 *
 * 8 tiles in a 4×2 hairline grid (matches the CPC/Master dashboard
 * pattern in /app/frontend/src/components/dashboards/). Period toggle
 * (Today / Week / Month) recalculates the period-aware tiles; the
 * remaining tiles are point-in-time and dim subtly to signal that the
 * toggle doesn't apply to them.
 *
 * Tile-click opens a right-side `<Sheet>` (drill-down drawer). Drawer
 * content is produced by `<ProcurementDrillDrawer>` so this file stays
 * concerned with the grid + state only.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MetricTile } from "@/components/dashboards/MetricTile";
import { PeriodToggle } from "@/components/dashboards/PeriodToggle";
import { ProcurementDrillDrawer } from "./ProcurementDrillDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiles that recompute on period change. Used to gray-out point-in-time
// tiles on hover so the user understands the toggle's scope.
const PERIOD_AWARE_TILES = new Set(["new_rms", "price_increases", "spend"]);

// RAG thresholds — keep here so they're auditable in one place.
function _ragLowerBetter(value, amberAt, redAt) {
  if (value === 0 || value == null) return "green";
  if (value <= amberAt) return "amber";
  if (value <= redAt) return "amber";
  return "red";
}
function _ragPctLowerBetter(pct, amberAt = 5, redAt = 15) {
  if (pct == null) return "neutral";
  if (pct < amberAt) return "green";
  if (pct < redAt) return "amber";
  return "red";
}

function fmtInr(v) {
  if (v == null) return "—";
  const lakhs = v / 100000;
  if (Math.abs(lakhs) >= 1) return `₹${lakhs.toFixed(1)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

export function ProcurementDashboard() {
  const [period, setPeriod] = useState("month");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillKey, setDrillKey] = useState(null);  // which tile is opened

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/procurement-dashboard/summary?period=${period}`);
      setSummary(res.data);
    } catch (e) {
      toast.error("Failed to load procurement dashboard");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    // Defer the synchronous setLoading inside `fetchSummary` into a
    // microtask so the react-hooks/set-state-in-effect rule sees only
    // post-await state writes (semantically a no-op; React batches
    // both flushes into the same paint).
    const id = setTimeout(() => { fetchSummary(); }, 0);
    return () => clearTimeout(id);
  }, [fetchSummary]);

  const t = summary?.tiles || {};

  // ── Tile RAG / delta resolvers ──
  const tile1Rag = _ragPctLowerBetter(t.rms_without_bom?.pct);
  const tile3Rag = (() => {
    const c = t.price_increases?.count ?? 0;
    if (c === 0) return "green";
    if (c < 5) return "amber";
    return "red";
  })();
  const tile4Rag = _ragPctLowerBetter(t.missing_vendor_price?.pct);
  const tile5Rag = (() => {
    const c = t.shortages_without_po?.count ?? 0;
    if (c === 0) return "green";
    if (c <= 5) return "amber";
    return "red";
  })();
  const tile6Rag = (t.overdue_po_receipts?.count ?? 0) === 0 ? "green" : "red";
  const tile8Rag = _ragLowerBetter(t.stale_vendor_prices?.count, 5, 20);

  return (
    <div className="space-y-6" data-testid="procurement-dashboard">
      {/* Header + period toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black tracking-tight font-[Chivo]">Procurement Overview</h2>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            8 KPIs across hygiene + operational signals · click a tile to drill in
          </p>
        </div>
        <PeriodToggle period={period} onChange={setPeriod} />
      </div>

      {/* Tile grid — 4 columns hairline */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border" data-testid="procurement-tiles">
        {/* Tile 1 — RM without BOM */}
        <MetricTile
          label="RM without BOM"
          value={loading ? "…" : (t.rms_without_bom?.count ?? "—")}
          rag={tile1Rag}
          source="direct"
          subText={loading ? "" : `${t.rms_without_bom?.count} of ${t.rms_without_bom?.total} purchasable RMs in no BOM`}
          footerLeft={`${t.rms_without_bom?.pct ?? 0}% of total`}
          status={tile1Rag === "green" ? "HEALTHY" : tile1Rag === "amber" ? "AT RISK" : "BREACH"}
          onClick={() => setDrillKey("rms_without_bom")}
          testId="tile-rms-without-bom"
        />

        {/* Tile 2 — New RMs (period-aware) */}
        <MetricTile
          label={`New RMs · ${period}`}
          value={loading ? "…" : (t.new_rms?.count ?? "—")}
          delta={t.new_rms?.delta}
          rag="neutral"
          source="direct"
          subText={t.new_rms ? `+${t.new_rms.count} this ${period} (${t.new_rms.delta?.label})` : ""}
          footerLeft={`prev: ${t.new_rms?.prev_count ?? 0}`}
          status="INFO"
          onClick={() => setDrillKey("new_rms")}
          testId="tile-new-rms"
        />

        {/* Tile 3 — Price increases (period-aware) */}
        <MetricTile
          label={`Price ↑ · ${period}`}
          value={loading ? "…" : (t.price_increases?.count ?? "—")}
          rag={tile3Rag}
          source="direct"
          subText={t.price_increases ? `Avg Δ +${t.price_increases.avg_pct}% · highest +${t.price_increases.max_pct}%` : ""}
          footerLeft="cost-pressure signal"
          status={tile3Rag === "green" ? "OK" : tile3Rag === "amber" ? "AT RISK" : "BREACH"}
          onClick={() => setDrillKey("price_increases")}
          testId="tile-price-increases"
        />

        {/* Tile 4 — Missing vendor price */}
        <MetricTile
          label="Missing vendor price"
          value={loading ? "…" : (t.missing_vendor_price?.count ?? "—")}
          rag={tile4Rag}
          source="direct"
          subText={loading ? "" : `${t.missing_vendor_price?.count} RMs cannot be sourced`}
          footerLeft={`${t.missing_vendor_price?.pct ?? 0}% of purchasable RMs`}
          status={tile4Rag === "green" ? "HEALTHY" : tile4Rag === "amber" ? "AT RISK" : "BREACH"}
          onClick={() => setDrillKey("missing_vendor_price")}
          testId="tile-missing-vendor-price"
        />

        {/* Tile 5 — Shortages without PO */}
        <MetricTile
          label="Shortages needing PO"
          value={loading ? "…" : (t.shortages_without_po?.count ?? "—")}
          rag={tile5Rag}
          source="aggregated"
          subText={loading ? "" : `${t.shortages_without_po?.count} shortages with no PO raised`}
          footerLeft="from /cpc/rm-shortage-report"
          status={tile5Rag === "green" ? "HEALTHY" : tile5Rag === "amber" ? "AT RISK" : "BREACH"}
          onClick={() => setDrillKey("shortages_without_po")}
          testId="tile-shortages-without-po"
        />

        {/* Tile 6 — Overdue PO receipts */}
        <MetricTile
          label="Overdue PO receipts"
          value={loading ? "…" : (t.overdue_po_receipts?.count ?? "—")}
          rag={tile6Rag}
          source="direct"
          subText={t.overdue_po_receipts ? `${fmtInr(t.overdue_po_receipts.open_value_inr)} overdue · oldest ${t.overdue_po_receipts.oldest_age_days} days` : ""}
          footerLeft="SENT / PARTIAL past ETA"
          status={tile6Rag === "green" ? "HEALTHY" : "BREACH"}
          onClick={() => setDrillKey("overdue_po_receipts")}
          testId="tile-overdue-po-receipts"
        />

        {/* Tile 7 — Spend (period-aware) */}
        <MetricTile
          label={`Spend · ${period}`}
          value={loading ? "…" : fmtInr(t.spend?.value_inr)}
          delta={t.spend?.delta}
          rag="neutral"
          source="direct"
          subText={t.spend ? `${t.spend.po_count} POs · ${t.spend.delta?.label}` : ""}
          footerLeft={`prev: ${fmtInr(t.spend?.prev_value_inr)}`}
          status="INFO"
          onClick={() => setDrillKey("spend")}
          testId="tile-spend"
        />

        {/* Tile 8 — Stale vendor pricing */}
        <MetricTile
          label="Stale vendor prices"
          value={loading ? "…" : (t.stale_vendor_prices?.count ?? "—")}
          rag={tile8Rag}
          source="direct"
          subText={t.stale_vendor_prices ? `${t.stale_vendor_prices.count} prices > ${t.stale_vendor_prices.threshold_days}d old` : ""}
          footerLeft="re-quote due"
          status={tile8Rag === "green" ? "HEALTHY" : tile8Rag === "amber" ? "AT RISK" : "BREACH"}
          onClick={() => setDrillKey("stale_vendor_prices")}
          testId="tile-stale-vendor-prices"
        />
      </div>

      {/* Drill-down drawer */}
      {drillKey && (
        <ProcurementDrillDrawer
          tileKey={drillKey}
          period={period}
          open={!!drillKey}
          onClose={() => setDrillKey(null)}
          onChanged={() => { fetchSummary(); }}
        />
      )}
    </div>
  );
}

export default ProcurementDashboard;
